import React, { useState, useEffect } from 'react';
import { menuAPI, restaurantAPI } from '../../services/api';
import TimeInput12h from '../../components/TimeInput12h';

// Non-blocking toast — replaces window.alert() which permanently steals
// focus from webContents on Windows Electron, making all input fields
// unresponsive until the app is restarted.
function Toast({ message, type, onClose }) {
  if (!message) return null;
  const bg = type === 'error' ? '#FEF2F2' : '#F0FDF4';
  const border = type === 'error' ? '#FECACA' : '#BBF7D0';
  const color = type === 'error' ? '#B91C1C' : '#15803D';
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 99999,
      padding: '14px 24px', borderRadius: 10, background: bg,
      border: `1px solid ${border}`, color, fontWeight: 600,
      fontSize: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      display: 'flex', alignItems: 'center', gap: 12, maxWidth: 400,
    }}>
      <span>{message}</span>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 18, color, padding: 0, lineHeight: 1
      }}>×</button>
    </div>
  );
}

function MenuManagement() {
  const [menuItems, setMenuItems] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });

  // Non-blocking replacement for alert() — auto-dismisses after 4s
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 4000);
  };
  const [editingItem, setEditingItem] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  // Menu layout: 'list' (default, compact rows) or 'grid' (cards). Persisted.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('menu_view_mode') || 'list'; } catch (_) { return 'list'; }
  });
  const setView = (m) => {
    setViewMode(m);
    try { localStorage.setItem('menu_view_mode', m); } catch (_) {}
  };
  const [searchQuery, setSearchQuery] = useState('');
  // Availability filter: 'all' | 'on' (Available) | 'off' (Unavailable).
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  // When turning an item OFF, ask for how long. Holds the item being turned off.
  const [snoozeItem, setSnoozeItem] = useState(null);
  // When marking an item OUT OF STOCK, ask for how long. Holds that item.
  const [oosItem, setOosItem] = useState(null);
  const [customHours, setCustomHours] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Main Course',
    price: '',
    isVegetarian: false,
    isVegan: false,
    preparationTime: 20,
    availabilityTiming: {
      enabled: false,
      startTime: '00:00',
      endTime: '23:59'
    },
    variants: [],
    addons: []
  });
  const [newVariant, setNewVariant] = useState({ name: '', price: '' });
  const [newAddon, setNewAddon] = useState({ name: '', price: '' });
  const [loading, setLoading] = useState(true);

  // The 6 built-ins are just suggestions; the actual list also includes any
  // CUSTOM categories the restaurant has created (so they're filterable too).
  const DEFAULT_CATEGORIES = ['Appetizer', 'Main Course', 'Dessert', 'Beverages', 'Snacks', 'Sides'];
  const categories = ['All', ...new Set([...DEFAULT_CATEGORIES, ...menuItems.map((i) => i.category).filter(Boolean)])];

  useEffect(() => {
    fetchRestaurant();
  }, []);

  const fetchRestaurant = async () => {
    try {
      const response = await restaurantAPI.getMyRestaurant();
      const rest = response.data.data.restaurant;
      setRestaurant(rest);
      fetchMenuItems(rest._id);
    } catch (error) {
      console.error('Failed to fetch restaurant:', error);
      setLoading(false);
    }
  };

  const fetchMenuItems = async (restaurantId, bypassCache = false) => {
    try {
      // Add cache-busting parameter when bypassing cache (after updates)
      const params = bypassCache ? { _t: Date.now() } : {};
      const response = await menuAPI.getMenuItems(restaurantId, params);
      setMenuItems(response.data.data.menuItems);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size must be less than 5MB', 'error');
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category,
      price: item.price,
      isVegetarian: item.isVegetarian,
      isVegan: item.isVegan,
      preparationTime: item.preparationTime,
      availabilityTiming: item.availabilityTiming || {
        enabled: false,
        startTime: '00:00',
        endTime: '23:59'
      },
      variants: item.variants || [],
      addons: item.addons || []
    });
    if (item.image) {
      setImagePreview(item.image);
    }
    setNewVariant({ name: '', price: '' });
    setNewAddon({ name: '', price: '' });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      category: 'Main Course',
      price: '',
      isVegetarian: false,
      isVegan: false,
      preparationTime: 20,
      availabilityTiming: {
        enabled: false,
        startTime: '00:00',
        endTime: '23:59'
      },
      variants: [],
      addons: []
    });
    setSelectedImage(null);
    setImagePreview(null);
    setNewVariant({ name: '', price: '' });
    setNewAddon({ name: '', price: '' });
    setShowForm(false);
  };

  const addVariant = () => {
    if (newVariant.name.trim() && newVariant.price !== '') {
      setFormData({
        ...formData,
        variants: [...formData.variants, { name: newVariant.name.trim(), price: parseInt(newVariant.price, 10) }]
      });
      setNewVariant({ name: '', price: '' });
    }
  };

  const removeVariant = (index) => {
    setFormData({
      ...formData,
      variants: formData.variants.filter((_, i) => i !== index)
    });
  };

  const addAddon = () => {
    if (newAddon.name.trim() && newAddon.price !== '') {
      setFormData({
        ...formData,
        addons: [...formData.addons, { name: newAddon.name.trim(), price: parseInt(newAddon.price, 10) }]
      });
      setNewAddon({ name: '', price: '' });
    }
  };

  const removeAddon = (index) => {
    setFormData({
      ...formData,
      addons: formData.addons.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // CLIENT-SIDE VALIDATION (defence-in-depth; server must also enforce)
    const priceNum = parseFloat(formData.price);
    if (Number.isNaN(priceNum) || priceNum <= 0) {
      showToast('Price must be a number greater than 0', 'error');
      return;
    }
    if (priceNum > 50000) {
      showToast('Price looks unreasonably high (> ₹50,000). Please double-check.', 'error');
      return;
    }
    if (!formData.name || formData.name.trim().length < 2) {
      showToast('Menu item name must be at least 2 characters', 'error');
      return;
    }
    if (!formData.category) {
      showToast('Please select a category', 'error');
      return;
    }
    // Variants and addons must also have positive prices if present
    for (const v of (formData.variants || [])) {
      const vp = parseFloat(v.price);
      if (v.name && (Number.isNaN(vp) || vp <= 0)) {
        showToast(`Variant "${v.name}" must have a price greater than 0`, 'error');
        return;
      }
    }
    for (const a of (formData.addons || [])) {
      const ap = parseFloat(a.price);
      if (a.name && (Number.isNaN(ap) || ap < 0)) {
        showToast(`Addon "${a.name}" price cannot be negative`, 'error');
        return;
      }
    }

    try {
      if (editingItem) {
        // Update existing item - send JSON (like Flutter app does)
        const jsonData = {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          price: parseFloat(formData.price),
          isVegetarian: formData.isVegetarian,
          isVegan: formData.isVegan,
          preparationTime: formData.preparationTime,
          availabilityTiming: formData.availabilityTiming,
          variants: formData.variants,
          addons: formData.addons
        };

        await menuAPI.updateMenuItem(editingItem._id, jsonData);

        // Upload image separately if a new one was selected
        if (selectedImage) {
          const imageFormData = new FormData();
          imageFormData.append('image', selectedImage);
          await menuAPI.uploadImage(editingItem._id, imageFormData);
        }

        showToast('Menu item updated successfully!');
      } else {
        // Add new item - use FormData for image upload
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('price', formData.price);
        formDataToSend.append('isVegetarian', String(formData.isVegetarian));
        formDataToSend.append('isVegan', String(formData.isVegan));
        formDataToSend.append('preparationTime', formData.preparationTime);
        formDataToSend.append('availabilityTiming', JSON.stringify(formData.availabilityTiming));
        formDataToSend.append('variants', JSON.stringify(formData.variants));
        formDataToSend.append('addons', JSON.stringify(formData.addons));

        if (selectedImage) {
          formDataToSend.append('image', selectedImage);
        }

        await menuAPI.addMenuItem(formDataToSend);
        showToast('Menu item added successfully!');
      }

      handleCancelEdit();
      fetchMenuItems(restaurant._id, true); // Bypass cache after update
    } catch (error) {
      showToast(`Failed to ${editingItem ? 'update' : 'add'} menu item: ` + (error.response?.data?.message || error.message), 'error');
    }
  };

  // Toggling availability: turning an item OFF opens a dialog asking for how long
  // (a fixed number of hours, or until manually turned back on). Turning it ON is
  // immediate.
  const toggleAvailability = (itemId) => {
    const item = menuItems.find(i => i._id === itemId);
    if (!item) return;
    if (item.isAvailable) {
      setCustomHours('');
      setSnoozeItem(item); // open the "how long?" dialog
    } else {
      applyAvailability(itemId, true, 0); // turn back on immediately
    }
  };

  // Apply an availability change. isAvailable=false + unavailableHours>0 auto-turns
  // the item back on after that many hours; hours=0 means "until manually on".
  const applyAvailability = async (itemId, isAvailable, unavailableHours) => {
    try {
      await menuAPI.toggleAvailability(itemId, { isAvailable, unavailableHours: unavailableHours || 0 });
      setSnoozeItem(null);
      fetchMenuItems(restaurant._id, true); // Bypass cache after update
      if (!isAvailable) {
        showToast(unavailableHours > 0
          ? `Turned off — back on automatically in ${unavailableHours}h`
          : 'Turned off — back on automatically tomorrow');
      }
    } catch (error) {
      showToast('Failed to update availability', 'error');
    }
  };

  // Marking an item OUT of stock opens a dialog asking for how long (a fixed
  // number of hours, or until restocked). Marking it back IN stock is immediate.
  const toggleOutOfStock = (itemId) => {
    const item = menuItems.find(i => i._id === itemId);
    if (!item) return;
    if (item.outOfStock?.isOutOfStock) {
      applyOutOfStock(itemId, false, 0); // back in stock immediately
    } else {
      setCustomHours('');
      setOosItem(item); // open the "how long?" dialog
    }
  };

  const applyOutOfStock = async (itemId, isOutOfStock, outOfStockHours) => {
    try {
      await menuAPI.toggleOutOfStock(itemId, { isOutOfStock, outOfStockHours: outOfStockHours || 0 });
      setOosItem(null);
      fetchMenuItems(restaurant._id, true); // Bypass cache after update
      if (isOutOfStock) {
        showToast(outOfStockHours > 0
          ? `Out of stock — back automatically in ${outOfStockHours}h`
          : 'Out of stock until you restock it');
      }
    } catch (error) {
      showToast('Failed to update out of stock status', 'error');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await menuAPI.deleteMenuItem(itemId);
      showToast('Item deleted successfully');
      fetchMenuItems(restaurant._id, true); // Bypass cache after delete
    } catch (error) {
      showToast('Failed to delete item', 'error');
    }
  };

  // Items matching category + search (before the on/off filter) — used for the
  // on/off counts so the badges reflect the current category/search context.
  const catSearchMatches = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const onCount = catSearchMatches.filter(i => i.isAvailable).length;
  const offCount = catSearchMatches.filter(i => !i.isAvailable).length;
  const filteredMenuItems = catSearchMatches.filter(item =>
    availabilityFilter === 'all' ? true
      : availabilityFilter === 'on' ? item.isAvailable
        : !item.isAvailable
  );

  // "back in 1h 20m" from a future timestamp; '' if none/passed.
  const remainingLabel = (until) => {
    if (!until) return '';
    const ms = new Date(until).getTime() - Date.now();
    if (ms <= 0) return '';
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `back in ${h > 0 ? h + 'h ' : ''}${m}m`;
  };
  // Short label for a timed-unavailable item. '' when no active snooze.
  const snoozeLabel = (item) => (item.isAvailable ? '' : remainingLabel(item.unavailableUntil));
  // Short label for a timed out-of-stock item. '' when not timed / not OOS.
  const oosLabel = (item) => (item.outOfStock?.isOutOfStock ? remainingLabel(item.outOfStock?.until) : '');

  // Compact horizontal LIST ROW (default view). Same actions as the grid card:
  // thumbnail · name/price/category/desc · availability toggle · Edit/Stock/Delete.
  const renderListRow = (item) => (
    <div key={item._id} style={{
      background: 'white',
      borderRadius: '12px',
      border: '1px solid #E2E8F0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '12px 14px',
      opacity: item.outOfStock?.isOutOfStock ? 0.7 : 1
    }} className="fade-in">
      {/* Thumbnail */}
      <div style={{
        width: '64px', height: '64px', flexShrink: 0, borderRadius: '10px',
        overflow: 'hidden', background: '#F1F5F9', position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {item.image ? (
          <img src={item.image} alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <span style={{ fontSize: '24px' }}>🍽️</span>
        )}
      </div>

      {/* Name · price · category · description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {item.isVegetarian && <span title="Vegetarian" style={{ fontSize: '13px' }}>🌱</span>}
          <span style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A' }}>{item.name}</span>
          <span style={{ fontSize: '15px', fontWeight: 700, color: '#FF6961' }}>₹{item.price}</span>
          <span style={{
            padding: '2px 8px', background: '#F0FDF4', borderRadius: '6px',
            fontSize: '11px', fontWeight: 600, color: '#059669'
          }}>{item.category}</span>
          {item.availabilityTiming?.enabled && (
            <span style={{ padding: '2px 8px', background: '#FEF3C7', borderRadius: '6px', fontSize: '10px', fontWeight: 600, color: '#92400E' }}>
              🕐 {item.availabilityTiming.startTime}-{item.availabilityTiming.endTime}
            </span>
          )}
          {item.outOfStock?.isOutOfStock && (
            <span style={{ padding: '2px 8px', background: '#FEF2F2', borderRadius: '6px', fontSize: '10px', fontWeight: 600, color: '#DC2626' }}>
              {oosLabel(item) ? `📦 Out of stock · ${oosLabel(item)}` : '📦 Out of stock'}
            </span>
          )}
          {!item.isAvailable && (
            <span style={{ padding: '2px 8px', background: '#FEF2F2', borderRadius: '6px', fontSize: '10px', fontWeight: 600, color: '#DC2626' }}>
              {snoozeLabel(item) ? `⏱️ Off · ${snoozeLabel(item)}` : '⏸️ Off (manual)'}
            </span>
          )}
        </div>
        {(item.description || item.variants?.length > 0 || item.addons?.length > 0) && (
          <div style={{
            marginTop: '3px', color: '#64748B', fontSize: '12px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          }}>
            {item.description || ''}
            {item.variants?.length > 0 && <span> · 📏 {item.variants.map(v => `${v.name} (₹${v.price})`).join(', ')}</span>}
            {item.addons?.length > 0 && <span> · 🧀 {item.addons.map(a => `${a.name} (+₹${a.price})`).join(', ')}</span>}
          </div>
        )}
      </div>

      {/* Availability toggle */}
      <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', flexShrink: 0, cursor: 'pointer' }} title={item.isAvailable ? 'Available' : 'Unavailable'}>
        <input type="checkbox" checked={item.isAvailable} onChange={() => toggleAvailability(item._id)} style={{ opacity: 0, width: 0, height: 0 }} />
        <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: item.isAvailable ? '#10B981' : '#CBD5E1', transition: '0.3s', borderRadius: '24px' }}>
          <span style={{ position: 'absolute', height: '18px', width: '18px', left: item.isAvailable ? '22px' : '3px', bottom: '3px', background: 'white', transition: '0.3s', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </span>
      </label>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        <button onClick={() => handleEdit(item)} title="Edit" style={{ background: '#FFF1F0', color: '#FF6961', border: '1px solid #FFD9D6', borderRadius: '8px', padding: '8px 10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>✏️</button>
        <button onClick={() => toggleOutOfStock(item._id)} title={item.outOfStock?.isOutOfStock ? 'Mark back in stock' : 'Mark out of stock'} style={{ background: item.outOfStock?.isOutOfStock ? '#ECFDF5' : '#FFFBEB', color: item.outOfStock?.isOutOfStock ? '#059669' : '#D97706', border: `1px solid ${item.outOfStock?.isOutOfStock ? '#A7F3D0' : '#FDE68A'}`, borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {item.outOfStock?.isOutOfStock ? '✅ In Stock' : '📦 Out of Stock'}
        </button>
        <button onClick={() => deleteItem(item._id)} title="Delete" style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: '8px', padding: '8px 10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>🗑️</button>
      </div>
    </div>
  );

  if (loading) return <div className="loading">🔄 Loading menu...</div>;
  if (!restaurant) return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <div className="container">
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          marginTop: '24px'
        }}>
          <div style={{ fontSize: '80px', marginBottom: '16px' }}>❌</div>
          <h2 style={{ color: '#64748B', fontSize: '20px', fontWeight: '600' }}>No restaurant found</h2>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'success' })} />

      {/* "Turn off for how long?" dialog */}
      {snoozeItem && (
        <div
          onClick={() => setSnoozeItem(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 99998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A', margin: '0 0 4px' }}>Turn off “{snoozeItem.name}”</h3>
            <p style={{ color: '#64748B', fontSize: '14px', margin: '0 0 16px' }}>
              How long should it stay unavailable? It will turn back on automatically.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              {[1, 2, 4, 8, 12, 24].map(h => (
                <button key={h} onClick={() => applyAvailability(snoozeItem._id, false, h)} style={{
                  padding: '12px 8px', border: '1px solid #E2E8F0', borderRadius: '10px', background: '#F8FAFC',
                  color: '#0F172A', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                }}>{h < 24 ? `${h} hr${h > 1 ? 's' : ''}` : '1 day'}</button>
              ))}
            </div>

            {/* Custom hours */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
              <input
                type="number" min="1" max="720" placeholder="Custom hours"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                style={{ flex: 1, padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: '10px', fontSize: '14px', outline: 'none' }}
              />
              <button
                onClick={() => { const h = Number(customHours); if (h > 0) applyAvailability(snoozeItem._id, false, h); }}
                disabled={!(Number(customHours) > 0)}
                style={{ padding: '10px 16px', border: 'none', borderRadius: '10px', background: Number(customHours) > 0 ? '#FF6961' : '#E2E8F0', color: 'white', fontSize: '14px', fontWeight: 600, cursor: Number(customHours) > 0 ? 'pointer' : 'not-allowed' }}
              >Set</button>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => applyAvailability(snoozeItem._id, false, 0)} style={{
                flex: 1, padding: '12px', border: '1px solid #A7F3D0', borderRadius: '10px', background: '#ECFDF5',
                color: '#047857', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
              }}>🌅 Back tomorrow (default)</button>
              <button onClick={() => setSnoozeItem(null)} style={{
                padding: '12px 18px', border: '1px solid #E2E8F0', borderRadius: '10px', background: 'white',
                color: '#64748B', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* "Out of stock for how long?" dialog */}
      {oosItem && (
        <div
          onClick={() => setOosItem(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 99998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A', margin: '0 0 4px' }}>📦 Mark “{oosItem.name}” out of stock</h3>
            <p style={{ color: '#64748B', fontSize: '14px', margin: '0 0 16px' }}>
              How long will it be out of stock? It will come back automatically.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              {[1, 2, 4, 8, 12, 24].map(h => (
                <button key={h} onClick={() => applyOutOfStock(oosItem._id, true, h)} style={{
                  padding: '12px 8px', border: '1px solid #E2E8F0', borderRadius: '10px', background: '#F8FAFC',
                  color: '#0F172A', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                }}>{h < 24 ? `${h} hr${h > 1 ? 's' : ''}` : '1 day'}</button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
              <input
                type="number" min="1" max="720" placeholder="Custom hours"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                style={{ flex: 1, padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: '10px', fontSize: '14px', outline: 'none' }}
              />
              <button
                onClick={() => { const h = Number(customHours); if (h > 0) applyOutOfStock(oosItem._id, true, h); }}
                disabled={!(Number(customHours) > 0)}
                style={{ padding: '10px 16px', border: 'none', borderRadius: '10px', background: Number(customHours) > 0 ? '#F59E0B' : '#E2E8F0', color: 'white', fontSize: '14px', fontWeight: 600, cursor: Number(customHours) > 0 ? 'pointer' : 'not-allowed' }}
              >Set</button>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => applyOutOfStock(oosItem._id, true, 0)} style={{
                flex: 1, padding: '12px', border: '1px solid #FDE68A', borderRadius: '10px', background: '#FFFBEB',
                color: '#92400E', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
              }}>♻️ Until I restock it</button>
              <button onClick={() => setOosItem(null)} style={{
                padding: '12px 18px', border: '1px solid #E2E8F0', borderRadius: '10px', background: 'white',
                color: '#64748B', fontSize: '14px', fontWeight: 600, cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="container">
        {/* Header with Gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
          borderRadius: '16px',
          padding: '32px 24px',
          marginBottom: '24px',
          boxShadow: '0 4px 12px rgba(255, 105, 97, 0.15)',
          textAlign: 'center'
        }} className="fade-in">
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: 'white',
            marginBottom: '8px',
            letterSpacing: '-0.5px'
          }}>
            Menu Management
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: 0 }}>
            Add, edit, and manage your menu items
          </p>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '16px' }} className="fade-in">
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            border: '1px solid #E2E8F0'
          }}>
            <span style={{ fontSize: '20px' }}>🔍</span>
            <input
              type="text"
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '15px',
                color: '#0F172A',
                background: 'transparent'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  fontSize: '18px',
                  color: '#64748B'
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Category Filter Chips */}
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          marginBottom: '24px',
          padding: '4px 0',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} className="fade-in">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: selectedCategory === category
                  ? '#FF6961'
                  : 'white',
                color: selectedCategory === category
                  ? 'white'
                  : '#64748B',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                boxShadow: selectedCategory === category
                  ? '0 2px 8px rgba(255, 105, 97, 0.2)'
                  : '0 1px 3px rgba(0, 0, 0, 0.05)',
                transform: selectedCategory === category ? 'translateY(-1px)' : 'none'
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Availability filter: All / On (Available) / Off (Unavailable) */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }} className="fade-in">
          <div style={{ display: 'inline-flex', background: '#F1F5F9', borderRadius: '10px', padding: '4px', gap: '4px', flexWrap: 'wrap' }}>
            {[
              { key: 'all', label: `All (${catSearchMatches.length})`, active: '#0F172A' },
              { key: 'on', label: `🟢 On (${onCount})`, active: '#059669' },
              { key: 'off', label: `🔴 Off (${offCount})`, active: '#DC2626' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setAvailabilityFilter(opt.key)}
                style={{
                  padding: '7px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 600,
                  background: availabilityFilter === opt.key ? 'white' : 'transparent',
                  color: availabilityFilter === opt.key ? opt.active : '#64748B',
                  boxShadow: availabilityFilter === opt.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Add Item Button */}
        <div style={{ marginBottom: '24px', textAlign: 'center' }} className="fade-in">
          <button
            onClick={() => {
              if (showForm) {
                handleCancelEdit();
              } else {
                setEditingItem(null);
                setShowForm(true);
              }
            }}
            style={{
              background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '14px 28px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(255, 105, 97, 0.2)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 105, 97, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 105, 97, 0.2)';
            }}
          >
            <span style={{ fontSize: '18px' }}>{showForm ? '✖️' : '➕'}</span>
            <span>{showForm ? 'Cancel' : 'Add New Item'}</span>
          </button>
        </div>

        {/* Add Item Form */}
        {showForm && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            border: '1px solid #E2E8F0'
          }} className="fade-in">
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '24px',
              color: '#0F172A',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>{editingItem ? '✏️' : '➕'}</span>
              <span>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</span>
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0F172A'
                }}>🍽️ Item Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder=""
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '15px',
                    color: '#0F172A',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0F172A'
                }}>📝 Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder=""
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '15px',
                    color: '#0F172A',
                    outline: 'none',
                    resize: 'vertical',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#FF6961'}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0F172A'
                }}>📸 Item Image (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#0F172A',
                    outline: 'none'
                  }}
                />
                {imagePreview && (
                  <div style={{
                    marginTop: '12px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    maxWidth: '300px',
                    position: 'relative'
                  }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        // If editing an existing item with a server image,
                        // call the API to delete it from R2
                        if (editingItem && editingItem.image && !selectedImage) {
                          if (window.confirm('Remove this image? It will be deleted from the server.')) {
                            try {
                              await menuAPI.removeImage(editingItem._id);
                              // Immediately update local state so UI reflects removal
                              setImagePreview(null);
                              setEditingItem({ ...editingItem, image: null });
                              setMenuItems(prev => prev.map(item =>
                                item._id === editingItem._id ? { ...item, image: null } : item
                              ));
                              // Also refresh from server for consistency
                              fetchMenuItems(restaurant._id, true);
                            } catch (err) {
                              showToast('Failed to remove image: ' + (err.response?.data?.message || err.message), 'error');
                            }
                          }
                        } else {
                          // Just clearing a newly selected (not yet uploaded) image
                          setSelectedImage(null);
                          setImagePreview(null);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#EF4444',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
                <p style={{
                  fontSize: '12px',
                  color: '#64748B',
                  marginTop: '6px',
                  fontStyle: 'italic'
                }}>
                  Max file size: 5MB | Supported formats: JPG, PNG, GIF, WebP
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>🏷️ Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    list="rm-menu-categories"
                    placeholder="Pick a suggestion or type a custom category"
                    maxLength={50}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      color: '#0F172A',
                      outline: 'none',
                      background: 'white'
                    }}
                  />
                  <datalist id="rm-menu-categories">
                    {[...new Set([
                      ...categories.filter((c) => c !== 'All'),
                      ...menuItems.map((i) => i.category).filter(Boolean),
                    ])].map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>💰 Price (₹)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder=""
                    required
                    min="0"
                    onWheel={(e) => e.target.blur()}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      color: '#0F172A',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: '#0F172A'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.isVegetarian}
                    onChange={(e) => setFormData({ ...formData, isVegetarian: e.target.checked })}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#FF6961'
                    }}
                  />
                  <span>🌱 Vegetarian Item</span>
                </label>
              </div>

              {/* Variants Section */}
              <div style={{
                padding: '16px',
                background: '#F8FAFC',
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                marginBottom: '16px'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0F172A'
                }}>📏 Variants (Size options, etc.)</label>

                {/* Existing Variants */}
                {formData.variants.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    {formData.variants.map((variant, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: 'white',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        border: '1px solid #E2E8F0'
                      }}>
                        <span style={{ fontWeight: '500', color: '#0F172A' }}>{variant.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ color: '#10B981', fontWeight: '600' }}>₹{variant.price}</span>
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            style={{
                              background: '#FEE2E2',
                              color: '#DC2626',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Variant */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Variant name (e.g., Large)"
                    value={newVariant.name}
                    onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={newVariant.price}
                    onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value.replace(/\D/g, '') })}
                    min="0"
                    step="1"
                    onKeyDown={(e) => e.key === '.' && e.preventDefault()}
                    onWheel={(e) => e.target.blur()}
                    style={{
                      width: '100px',
                      padding: '10px 12px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={addVariant}
                    style={{
                      background: '#10B981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 16px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                  >
                    + Add
                  </button>
                </div>
              </div>

              {/* Addons Section */}
              <div style={{
                padding: '16px',
                background: '#FFF7ED',
                borderRadius: '8px',
                border: '1px solid #FED7AA',
                marginBottom: '16px'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0F172A'
                }}>🧀 Addons (Extra toppings, sides, etc.)</label>

                {/* Existing Addons */}
                {formData.addons.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    {formData.addons.map((addon, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: 'white',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        border: '1px solid #FED7AA'
                      }}>
                        <span style={{ fontWeight: '500', color: '#0F172A' }}>{addon.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ color: '#F59E0B', fontWeight: '600' }}>+₹{addon.price}</span>
                          <button
                            type="button"
                            onClick={() => removeAddon(index)}
                            style={{
                              background: '#FEE2E2',
                              color: '#DC2626',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Addon */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Addon name (e.g., Extra Cheese)"
                    value={newAddon.name}
                    onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #FED7AA',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={newAddon.price}
                    onChange={(e) => setNewAddon({ ...newAddon, price: e.target.value.replace(/\D/g, '') })}
                    min="0"
                    step="1"
                    onKeyDown={(e) => e.key === '.' && e.preventDefault()}
                    onWheel={(e) => e.target.blur()}
                    style={{
                      width: '100px',
                      padding: '10px 12px',
                      border: '1px solid #FED7AA',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={addAddon}
                    style={{
                      background: '#F59E0B',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 16px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                  >
                    + Add
                  </button>
                </div>
              </div>

              {/* Availability Timing (Optional) */}
              <div style={{
                padding: '16px',
                background: '#F0FDF4',
                borderRadius: '8px',
                border: '1px dashed #86EFAC',
                marginBottom: '16px'
              }}>
                <div style={{ marginBottom: formData.availabilityTiming.enabled ? '16px' : '0' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.availabilityTiming.enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        availabilityTiming: {
                          ...formData.availabilityTiming,
                          enabled: e.target.checked
                        }
                      })}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        accentColor: '#FF6961'
                      }}
                    />
                    <span>🕐 Set Specific Availability Time</span>
                  </label>
                  <p style={{
                    fontSize: '12px',
                    color: '#64748B',
                    marginTop: '4px',
                    marginLeft: '28px',
                    fontStyle: 'italic'
                  }}>
                    E.g., Breakfast items available 8:00 AM - 11:00 AM only
                  </p>
                </div>

                {formData.availabilityTiming.enabled && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0F172A'
                      }}>🕐 Start Time</label>
                      <TimeInput12h
                        value={formData.availabilityTiming.startTime}
                        onChange={(val) => setFormData({
                          ...formData,
                          availabilityTiming: {
                            ...formData.availabilityTiming,
                            startTime: val
                          }
                        })}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0F172A'
                      }}>🕐 End Time</label>
                      <TimeInput12h
                        value={formData.availabilityTiming.endTime}
                        onChange={(val) => setFormData({
                          ...formData,
                          availabilityTiming: {
                            ...formData.availabilityTiming,
                            endTime: val
                          }
                        })}
                      />
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '14px 28px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
                }}
              >
                {editingItem ? '✅ Update Menu Item' : '✅ Add Menu Item'}
              </button>
            </form>
          </div>
        )}

        {/* View toggle: List (default) / Grid */}
        {!showForm && filteredMenuItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }} className="fade-in">
            <div style={{ display: 'inline-flex', background: '#F1F5F9', borderRadius: '10px', padding: '4px', gap: '4px' }}>
              <button onClick={() => setView('list')} style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', fontWeight: 600,
                background: viewMode === 'list' ? 'white' : 'transparent',
                color: viewMode === 'list' ? '#0F172A' : '#64748B',
                boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}>☰ List</button>
              <button onClick={() => setView('grid')} style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', fontWeight: 600,
                background: viewMode === 'grid' ? 'white' : 'transparent',
                color: viewMode === 'grid' ? '#0F172A' : '#64748B',
                boxShadow: viewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}>▦ Grid</button>
            </div>
          </div>
        )}

        {/* Menu Items — list rows (default) or grid cards */}
        <div style={viewMode === 'list'
          ? { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }
          : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }
        }>
          {filteredMenuItems.map(item => viewMode === 'list' ? renderListRow(item) : (
            <div key={item._id} style={{
              background: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              border: '1px solid #E2E8F0',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column'
            }} className="fade-in">
              {/* Item Image */}
              {item.image && (
                <div style={{
                  width: '100%',
                  height: '180px',
                  overflow: 'hidden',
                  background: '#F1F5F9',
                  position: 'relative'
                }}>
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />

                  {/* Vegetarian Badge */}
                  {item.isVegetarian && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      padding: '4px 12px',
                      background: '#10B981',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                    }}>
                      <span>🌱</span>
                      <span>VEG</span>
                    </div>
                  )}
                </div>
              )}

              {/* Content */}
              <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Name and Price */}
                <div style={{ marginBottom: '8px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#0F172A',
                    marginBottom: '4px',
                    lineHeight: '1.3'
                  }}>
                    {item.name}
                  </h3>
                  <p style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#FF6961',
                    margin: 0
                  }}>
                    ₹{item.price}
                  </p>
                </div>

                {/* Description */}
                <p style={{
                  color: '#64748B',
                  fontSize: '13px',
                  marginBottom: '12px',
                  lineHeight: '1.5',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {item.description || 'No description available'}
                </p>

                {/* Category Badge */}
                <div style={{ marginBottom: '12px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    background: '#F0FDF4',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#059669'
                  }}>
                    {item.category}
                  </span>

                  {/* Availability Timing Badge */}
                  {item.availabilityTiming?.enabled && (
                    <span style={{
                      display: 'inline-block',
                      marginLeft: '8px',
                      padding: '4px 12px',
                      background: '#FEF3C7',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#92400E'
                    }}>
                      🕐 {item.availabilityTiming.startTime} - {item.availabilityTiming.endTime}
                    </span>
                  )}
                </div>

                {/* Variants & Addons Display */}
                {(item.variants?.length > 0 || item.addons?.length > 0) && (
                  <div style={{ marginBottom: '12px', fontSize: '12px' }}>
                    {item.variants?.length > 0 && (
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ color: '#64748B' }}>📏 Variants: </span>
                        <span style={{ color: '#0F172A', fontWeight: '500' }}>
                          {item.variants.map(v => `${v.name} (₹${v.price})`).join(', ')}
                        </span>
                      </div>
                    )}
                    {item.addons?.length > 0 && (
                      <div>
                        <span style={{ color: '#64748B' }}>🧀 Addons: </span>
                        <span style={{ color: '#0F172A', fontWeight: '500' }}>
                          {item.addons.map(a => `${a.name} (+₹${a.price})`).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Availability Toggle */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  background: item.isAvailable ? '#F0FDF4' : '#FEF2F2',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>
                      {item.isAvailable ? '✅' : '❌'}
                    </span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: item.isAvailable ? '#059669' : '#DC2626'
                    }}>
                      {item.isAvailable
                        ? 'Available'
                        : (snoozeLabel(item) ? `Off · ${snoozeLabel(item)}` : 'Off (until manual)')}
                    </span>
                  </div>
                  <label style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '48px',
                    height: '28px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={item.isAvailable}
                      onChange={() => toggleAvailability(item._id)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: item.isAvailable ? '#10B981' : '#CBD5E1',
                      transition: '0.3s',
                      borderRadius: '28px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '20px',
                        width: '20px',
                        left: item.isAvailable ? '24px' : '4px',
                        bottom: '4px',
                        background: 'white',
                        transition: '0.3s',
                        borderRadius: '50%',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                      }} />
                    </span>
                  </label>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: 'auto' }}>
                  <button
                    onClick={() => handleEdit(item)}
                    style={{
                      background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      boxShadow: '0 2px 8px rgba(255, 105, 97, 0.15)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <span>✏️</span>
                    <span>Edit</span>
                  </button>

                  <button
                    onClick={() => toggleOutOfStock(item._id)}
                    style={{
                      background: item.outOfStock?.isOutOfStock
                        ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      boxShadow: item.outOfStock?.isOutOfStock
                        ? '0 2px 8px rgba(16, 185, 129, 0.15)'
                        : '0 2px 8px rgba(245, 158, 11, 0.15)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <span>{item.outOfStock?.isOutOfStock ? '✅' : '📦'}</span>
                    <span style={{ fontSize: '13px' }}>
                      {item.outOfStock?.isOutOfStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </button>
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => deleteItem(item._id)}
                  style={{
                    marginTop: '8px',
                    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.15)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <span>🗑️</span>
                  <span>Delete Item</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredMenuItems.length === 0 && !showForm && (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            border: '1px solid #E2E8F0'
          }} className="fade-in">
            <div style={{ fontSize: '80px', marginBottom: '16px' }}>
              {searchQuery || selectedCategory !== 'All' || availabilityFilter !== 'all' ? '🔍' : '🍽️'}
            </div>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#0F172A',
              marginBottom: '8px'
            }}>
              {searchQuery || selectedCategory !== 'All' || availabilityFilter !== 'all'
                ? 'No menu items found'
                : 'No menu items yet'}
            </h2>
            <p style={{
              color: '#64748B',
              fontSize: '15px',
              marginBottom: '24px',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              {searchQuery || selectedCategory !== 'All' || availabilityFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Click "Add New Item" to create your first menu item'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MenuManagement;

/* snooze feature deploy sync */
